/**
 * Create / Edit Finance Entry dialog.
 * Shared by the Finance Reports page and a project's Financials tab so both record entries
 * through exactly the same form and endpoints.
 */
import React, { useState, useEffect } from 'react';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Textarea } from '../../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../../ui/dialog';
import { Switch } from '../../ui/switch';
import { api } from '../../../context/AuthContext';
import { toast } from 'sonner';
import { phaseOptions, inferPhaseFromAccount } from '../../../lib/projectPhases';

const accountTypeConfig = {
  asset: { label: 'Asset' },
  liability: { label: 'Liability' },
  equity: { label: 'Equity' },
  revenue: { label: 'Revenue' },
  expense: { label: 'Expense' },
};

const FinanceEntryFormDialog = ({ open, onOpenChange, entry, projectId, projectType, onSave }) => {
  const isEdit = !!entry?.id;
  const [loading, setLoading] = useState(false);
  const [accounts, setAccounts] = useState([]);
  const phases = phaseOptions(projectType);

  useEffect(() => {
    if (!open) return;
    api.get('/admin/v2/finance/accounts')
      .then((res) => setAccounts(res.data?.accounts || []))
      .catch(() => setAccounts([]));
  }, [open]);

  const selectAccount = (code) => {
    const account = accounts.find((a) => a.code === code);
    setFormData((prev) => ({
      ...prev,
      account_code: code,
      account_name: account ? account.name : prev.account_name,
      account_type: account ? account.type : prev.account_type,
      category: account ? account.category : prev.category,
      // The account says which part of the project this belongs to
      phase: (account && (account.phase || inferPhaseFromAccount(code))) || inferPhaseFromAccount(code) || prev.phase,
    }));
  };
  
  const [formData, setFormData] = useState({
    entry_type: 'actual',
    entry_date: new Date().toISOString().split('T')[0],
    account_code: '',
    account_name: '',
    account_type: 'expense',
    description: '',
    amount: '',
    is_debit: true,
    category: '',
    phase: '',
    reference: '',
    notes: '',
    status: 'posted',
  });

  useEffect(() => {
    if (entry) {
      setFormData({
        entry_type: entry.entry_type || 'actual',
        entry_date: entry.entry_date || new Date().toISOString().split('T')[0],
        account_code: entry.account_code || '',
        account_name: entry.account_name || '',
        account_type: entry.account_type || 'expense',
        description: entry.description || '',
        amount: entry.amount?.toString() || '',
        is_debit: entry.is_debit !== false,
        category: entry.category || '',
        phase: entry.phase || inferPhaseFromAccount(entry.account_code) || '',
        reference: entry.reference || '',
        notes: entry.notes || '',
        status: entry.status || 'posted',
      });
    } else {
      setFormData({
        entry_type: 'actual',
        entry_date: new Date().toISOString().split('T')[0],
        account_code: '',
        account_name: '',
        account_type: 'expense',
        description: '',
        amount: '',
        is_debit: true,
        category: '',
        phase: '',
        reference: '',
        notes: '',
        status: 'posted',
      });
    }
  }, [entry, open]);

  const handleSubmit = async () => {
    if (!formData.account_code.trim()) {
      toast.error('Please choose an account');
      return;
    }
    if (!isEdit && !projectId) {
      toast.error('Select a project first - entries are recorded against a project');
      return;
    }
    if (!formData.amount || parseFloat(formData.amount) <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }
    if (!formData.description.trim()) {
      toast.error('Description is required');
      return;
    }
    if (!formData.phase) {
      toast.error('Choose the project phase this entry belongs to');
      return;
    }

    setLoading(true);
    try {
      const payload = {
        project_id: projectId,
        entry_type: formData.entry_type,
        entry_date: formData.entry_date,
        account_code: formData.account_code,
        account_name: formData.account_name || formData.account_code,
        account_type: formData.account_type,
        description: formData.description,
        amount: formData.amount,
        is_debit: formData.is_debit,
        category: formData.category || null,
        phase: formData.phase || null,
        reference: formData.reference || null,
        notes: formData.notes || null,
        status: formData.status,
      };

      if (isEdit) {
        const res = await api.put(`/admin/v2/finance/entries/${entry.id}`, payload);
        toast.success(
          res.data?.plan_version
            ? `Entry updated · budget now on v${res.data.plan_version}`
            : 'Entry updated successfully'
        );
      } else {
        // Budgets and actuals are recorded on their own project endpoints
        const path = formData.entry_type === 'budget' ? 'budgets' : 'actuals';
        const res = await api.post(`/admin/v2/finance/projects/${projectId}/${path}`, payload);
        toast.success(
          res.data?.plan_version
            ? `Entry created · budget now on v${res.data.plan_version}`
            : 'Entry created successfully'
        );
      }
      
      onSave();
      onOpenChange(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save entry');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px]">
        <DialogHeader>
          <DialogTitle>{isEdit ? 'Edit Finance Entry' : 'Create Finance Entry'}</DialogTitle>
          <DialogDescription>
            {isEdit ? 'Update finance entry details.' : 'Add a new budget or actual entry.'}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Entry Type</Label>
              <Select value={formData.entry_type} onValueChange={(v) => setFormData({...formData, entry_type: v})}>
                <SelectTrigger data-testid="finance-entry-type-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {/* Only budgets and actuals can be created here; journals have their own flow */}
                  <SelectItem value="budget">Budget</SelectItem>
                  <SelectItem value="actual">Actual</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Entry Date *</Label>
              <Input
                type="date"
                value={formData.entry_date}
                onChange={(e) => setFormData({...formData, entry_date: e.target.value})}
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Account *</Label>
              <Select value={formData.account_code} onValueChange={selectAccount}>
                <SelectTrigger data-testid="finance-account-select">
                  <SelectValue placeholder="Choose an account" />
                </SelectTrigger>
                <SelectContent className="max-h-72">
                  {accounts.map((a) => (
                    <SelectItem key={a.code} value={a.code} data-testid={`finance-account-option-${a.code}`}>
                      {a.code} — {a.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Account Name</Label>
              <Input
                value={formData.account_name}
                onChange={(e) => setFormData({...formData, account_name: e.target.value})}
                placeholder="Renovation Costs"
                data-testid="finance-account-name-input"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Project Phase *</Label>
              <Select value={formData.phase} onValueChange={(v) => setFormData({...formData, phase: v})}>
                <SelectTrigger data-testid="finance-phase-select">
                  <SelectValue placeholder="Choose a phase" />
                </SelectTrigger>
                <SelectContent>
                  {phases.map((p) => (
                    <SelectItem key={p.value} value={p.value} data-testid={`finance-phase-option-${p.value}`}>
                      {p.label}{!p.applicable && ' (not typical for this strategy)'}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-foreground-muted">
                Separates Development / Renovation performance from Operation performance.
              </p>
            </div>
            <div className="space-y-2">
              <Label>Category</Label>
              <Input
                value={formData.category}
                onChange={(e) => setFormData({...formData, category: e.target.value})}
                placeholder="Construction, Operations, Revenue..."
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label>Account Type</Label>
            <Select value={formData.account_type} onValueChange={(v) => setFormData({...formData, account_type: v})}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {Object.entries(accountTypeConfig).map(([key, config]) => (
                  <SelectItem key={key} value={key}>{config.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label>Description *</Label>
            <Textarea
              value={formData.description}
              onChange={(e) => setFormData({...formData, description: e.target.value})}
              placeholder="Description of this entry..."
              rows={2}
              data-testid="finance-description-input"
            />
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label>Amount (€) *</Label>
              <Input
                type="number"
                value={formData.amount}
                onChange={(e) => setFormData({...formData, amount: e.target.value})}
                placeholder="10000"
                data-testid="finance-amount-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Debit/Credit</Label>
              <div className="flex items-center gap-2 pt-2">
                <Switch
                  checked={formData.is_debit}
                  onCheckedChange={(v) => setFormData({...formData, is_debit: v})}
                />
                <span className="text-sm">{formData.is_debit ? 'Debit' : 'Credit'}</span>
              </div>
            </div>
            <div className="space-y-2">
              <Label>Status</Label>
              <Select value={formData.status} onValueChange={(v) => setFormData({...formData, status: v})}>
                <SelectTrigger data-testid="finance-status-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="pending_approval">Pending approval</SelectItem>
                  <SelectItem value="approved">Approved</SelectItem>
                  <SelectItem value="posted">Posted</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Reference</Label>
              <Input
                value={formData.reference}
                onChange={(e) => setFormData({...formData, reference: e.target.value})}
                placeholder="INV-2026-001, PO-123..."
              />
            </div>
            <div className="space-y-2">
              <Label>Notes</Label>
              <Input
                value={formData.notes}
                onChange={(e) => setFormData({...formData, notes: e.target.value})}
                placeholder="Additional notes..."
              />
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="finance-entry-cancel-btn">Cancel</Button>
          <Button onClick={handleSubmit} disabled={loading} data-testid="finance-entry-submit-btn">
            {loading ? 'Saving...' : isEdit ? 'Save Changes' : 'Create Entry'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export { FinanceEntryFormDialog };
export default FinanceEntryFormDialog;
